import { useState, useEffect } from "react";

import { formatDistanceToNow } from "date-fns";
import { Loader2, Edit, Archive, Trash2, Eye, Send, Sparkles, GitBranch, Wand2, Lightbulb, Plus, ChevronDown } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { Textarea } from "@/components/ui/textarea";
import { SaveIndicator } from "@/components/wizard/SaveIndicator";
import { useAdminEdgeFunction } from "@/hooks/useAdminEdgeFunction";
import { useCrud } from "@/hooks/useCrud";
import { useFormPersistence } from "@/hooks/useFormPersistence";
import { supabase } from "@/integrations/supabase/client";


import { BoardAnalytics } from "./BoardAnalytics";
import { BoardPreview } from "./BoardPreview";
import { BoardQualityScore } from "./BoardQualityScore";
import { BoardTemplateSuggestions } from "./BoardTemplateSuggestions";
import { CrudCard } from "./CrudCard";
import { CrudManager } from "./CrudManager";
import { PersonaEditor } from "./PersonaEditor";
import { CrudItemBase } from "./types/crud";


type PrebuiltPersona = {
  role_name: string;
  mission: string;
  principles: string;
  competencies: string;
  key_relationships: string;
  typical_questions: string;
};

type PrebuiltBoard = CrudItemBase & {
  personas: PrebuiltPersona[];
  replaced_by_board_id?: string;
  parent_board_id?: string;
};

export const PrebuiltBoardsManager = () => {
  const { invokeAdmin, isLoading: adminLoading, isAdmin } = useAdminEdgeFunction();
  const {
    items: boards,
    isLoading: loading,
    createItem: createBoard,
    updateItem: updateBoard,
    deleteItem: deleteBoard,
  } = useCrud<PrebuiltBoard>({
    tableName: "prebuilt_boards",
    queryKey: ["prebuilt_boards"],
    order: { column: "created_at", options: { ascending: false } },
  });
  const [editingBoard, setEditingBoard] = useState<PrebuiltBoard | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [boardToArchive, setBoardToArchive] = useState<PrebuiltBoard | null>(null);
  const [replacementBoardId, setReplacementBoardId] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewBoard, setPreviewBoard] = useState<PrebuiltBoard | null>(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [enhancingDescription, setEnhancingDescription] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);

  // Form persistence for board generation
  const {
    values: generationForm,
    updateValues: updateGenerationForm,
    clearFormData: clearGenerationForm,
    lastSaved,
    hasSavedData: hasSavedGenerationData,
  } = useFormPersistence(
    {
      generationType: "",
      generationDescription: "",
    },
    {
      formId: "prebuilt-board-generation",
      enabled: true,
    }
  );


  const handleGenerateBoard = async () => {
    if (!generationForm.generationType.trim()) {
      toast.error("Please enter a board type");
      return;
    }

    setIsGenerating(true);
    const { data, error } = await invokeAdmin("generate-board", {
      body: {
        generationMode: 'topic',
        boardType: generationForm.generationType,
        description: generationForm.generationDescription,
        stream: false,
      },
      showErrorToast: true,
    });
    setIsGenerating(false);

    if (error) return;

    toast.success("Board generated successfully!");
    setEditingBoard({
      id: "",
      name: data.boardDetails.name,
      description: data.boardDetails.description,
      personas: data.personas,
      status: "draft",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      version: 1,
      is_latest_version: true,
    });
    clearGenerationForm();
    setIsDialogOpen(true);
  };

  const handleSaveBoard = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingBoard) return;

    const boardData = {
      name: editingBoard.name,
      description: editingBoard.description,
      personas: editingBoard.personas,
      status: editingBoard.status,
    };

    if (editingBoard.id) {
      await updateBoard({ id: editingBoard.id, updates: boardData });
    } else {
      await createBoard(boardData as any);
    }

    setIsDialogOpen(false);
    setEditingBoard(null);
  };

  const handlePublish = async (boardId: string) => {
    await updateBoard({ id: boardId, updates: { status: "published" } });
  };

  const handleArchive = async () => {
    if (!boardToArchive) return;

    try {
      // If replacement board is selected, update all user boards
      if (replacementBoardId) {
        const { error: updateError } = await supabase
          .from("boards")
          .update({ 
            name: boards.find(b => b.id === replacementBoardId)?.name || "Updated Board"
          })
          .eq("name", boardToArchive.name);

        if (updateError) throw updateError;
      }

      await updateBoard({
        id: boardToArchive.id,
        updates: {
          status: "archived",
          replaced_by_board_id: replacementBoardId || null,
        },
      });

      setArchiveDialogOpen(false);
      setBoardToArchive(null);
      setReplacementBoardId("");
    } catch (error) {
      console.error("Error archiving board:", error);
      toast.error("Failed to archive board");
    }
  };

  const handleDelete = async (boardId: string) => {
    if (!confirm("Are you sure you want to delete this board? This cannot be undone.")) {
      return;
    }

    await deleteBoard(boardId);
  };

  const openArchiveDialog = (board: PrebuiltBoard) => {
    setBoardToArchive(board);
    setArchiveDialogOpen(true);
  };

  const handleCreateVersion = async (boardId: string) => {
    const board = boards.find(b => b.id === boardId);
    if (!board) return;

    try {
      const { data, error } = await supabase.rpc("create_board_version", {
        board_id: boardId,
        new_name: board.name,
        new_description: board.description,
        new_personas: board.personas as any,
      });

      if (error) throw error;

      toast.success(`New version created (v${board.version + 1})`);

      // Open the new version for editing
      const newBoard = boards.find(b => b.id === data);
      if (newBoard) {
        setEditingBoard(newBoard);
        setIsDialogOpen(true);
      }
    } catch (error) {
      console.error("Error creating version:", error);
      toast.error("Failed to create new version");
    }
  };

  const enhanceDescription = async () => {
    if (!editingBoard) return;
    
    setEnhancingDescription(true);
    const { data, error } = await invokeAdmin("enhance-board-content", {
      body: {
        type: "description",
        content: editingBoard.description,
        context: editingBoard.name,
      },
      showErrorToast: true,
    });
    setEnhancingDescription(false);

    if (error) return;
    
    setEditingBoard({ ...editingBoard, description: data.enhanced.trim() });
    toast.success("Description enhanced!");
  };

  const addPersona = () => {
    if (!editingBoard) return;
    
    const newPersona: PrebuiltPersona = {
      role_name: "",
      mission: "",
      principles: "",
      competencies: "",
      key_relationships: "",
      typical_questions: "",
    };
    
    setEditingBoard({
      ...editingBoard,
      personas: [...editingBoard.personas, newPersona],
    });
  };

  const updatePersona = (index: number, updatedPersona: PrebuiltPersona) => {
    if (!editingBoard) return;
    
    const newPersonas = [...editingBoard.personas];
    newPersonas[index] = updatedPersona;
    setEditingBoard({ ...editingBoard, personas: newPersonas });
  };

  const deletePersona = (index: number) => {
    if (!editingBoard) return;
    
    const newPersonas = editingBoard.personas.filter((_, i) => i !== index);
    setEditingBoard({ ...editingBoard, personas: newPersonas });
  };

  const handleSelectTemplate = async (suggestion: any) => {
    const { data, error } = await invokeAdmin("generate-board", {
      body: {
        boardType: suggestion.name,
        description: `${suggestion.description} Target: ${suggestion.targetAudience}`,
      },
      showErrorToast: true,
    });

    if (error) return;

    toast.success("Template board generated!");
    setEditingBoard({
      id: "",
      name: data.name,
      description: data.description,
      personas: data.personas,
      status: "draft",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      version: 1,
      is_latest_version: true,
    });
    setShowSuggestions(false);
    setIsDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <SaveIndicator lastSaved={lastSaved} />
      <CrudManager
        title="Prebuilt Boards"
        description="Manage template boards for users"
        onCreateNew={() => setEditingBoard({
          id: "",
          name: "",
          description: "",
          personas: [],
          status: "draft",
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          version: 1,
          is_latest_version: true,
        })}
        createLabel="Create Board"
        showStats={showAnalytics}
        onToggleStats={() => setShowAnalytics(!showAnalytics)}
        additionalActions={
          <Button
            variant="outline"
            onClick={() => setShowSuggestions(!showSuggestions)}
          >
            <Lightbulb className="h-4 w-4 mr-2" />
            {showSuggestions ? "Hide" : "Show"} Suggestions
          </Button>
        }
        statsSection={<BoardAnalytics />}
        aiGenerationSection={
          <Collapsible open={!isCollapsed} onOpenChange={(open) => setIsCollapsed(!open)}>
            <CollapsibleTrigger asChild>
              <Button variant="outline" className="w-full justify-between mb-4">
                <span className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  AI Board Generator
                </span>
                <ChevronDown className={`h-4 w-4 transition-transform ${!isCollapsed ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Sparkles className="h-5 w-5" />
                        AI Board Generator
                      </CardTitle>
                      <CardDescription>
                        Generate a new advisory board using AI
                        {hasSavedGenerationData && lastSaved && (
                          <span className="block text-xs mt-1">
                            Draft saved {formatDistanceToNow(lastSaved, { addSuffix: true })}
                          </span>
                        )}
                      </CardDescription>
                    </div>
                    {hasSavedGenerationData && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          clearGenerationForm();
                          toast.success("Draft cleared");
                        }}
                        className="text-muted-foreground"
                      >
                        Clear Draft
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="boardType">Board Type</Label>
                    <Input
                      id="boardType"
                      placeholder="e.g., SaaS Startup, Restaurant, Real Estate"
                      value={generationForm.generationType}
                      onChange={(e) => updateGenerationForm({ generationType: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Additional Context (Optional)</Label>
                    <Textarea
                      id="description"
                      placeholder="Describe the specific focus or industry details..."
                      value={generationForm.generationDescription}
                      onChange={(e) => updateGenerationForm({ generationDescription: e.target.value })}
                    />
                  </div>
                  <Button
                    onClick={handleGenerateBoard}
                    disabled={isGenerating || !generationForm.generationType.trim()}
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        Generate Board
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {showSuggestions && (
                <BoardTemplateSuggestions onSelectTemplate={handleSelectTemplate} />
              )}
            </CollapsibleContent>
          </Collapsible>
        }
      >
        {boards.map((board) => (
          <CrudCard
            key={board.id}
            item={board}
            additionalInfo={
              <p className="text-sm text-muted-foreground mt-2">
                {board.personas.length} personas • Created{" "}
                {new Date(board.created_at).toLocaleDateString()}
              </p>
            }
            actions={[
              {
                label: '',
                icon: Eye,
                onClick: () => setPreviewBoard(board),
                variant: 'outline',
                title: 'Preview board',
              },
              {
                label: 'Publish',
                icon: Send,
                onClick: () => handlePublish(board.id),
                hidden: board.status !== 'draft',
              },
              {
                label: 'New Version',
                icon: GitBranch,
                onClick: () => handleCreateVersion(board.id),
                variant: 'outline',
                title: 'Create new version',
                hidden: board.status !== 'published' || !board.is_latest_version,
              },
              {
                label: '',
                icon: Edit,
                onClick: () => {
                  setEditingBoard(board);
                  setIsDialogOpen(true);
                },
                variant: 'outline',
                title: 'Edit',
              },
              {
                label: '',
                icon: Archive,
                onClick: () => openArchiveDialog(board),
                variant: 'outline',
                title: 'Archive',
                hidden: board.status !== 'published',
              },
              {
                label: '',
                icon: Trash2,
                onClick: () => handleDelete(board.id),
                variant: 'outline',
                title: 'Delete',
                hidden: board.status === 'published',
              },
            ]}
          />
        ))}
      </CrudManager>


      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingBoard?.id ? "Edit Board" : "Create New Board"}
            </DialogTitle>
            <DialogDescription>
              Define the board template that users can select when creating a new board
            </DialogDescription>
          </DialogHeader>
          {editingBoard && (
            <form onSubmit={handleSaveBoard} className="space-y-4">
              <div>
                <Label htmlFor="name">Board Name</Label>
                <Input
                  id="name"
                  value={editingBoard.name}
                  onChange={(e) =>
                    setEditingBoard({ ...editingBoard, name: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="description">Description</Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={enhanceDescription}
                    disabled={enhancingDescription || !editingBoard.description}
                  >
                    {enhancingDescription ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <>
                        <Wand2 className="h-3 w-3 mr-1" />
                        Enhance
                      </>
                    )}
                  </Button>
                </div>
                <Textarea
                  id="description"
                  value={editingBoard.description}
                  onChange={(e) =>
                    setEditingBoard({ ...editingBoard, description: e.target.value })
                  }
                  required
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Personas</Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={addPersona}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Persona
                  </Button>
                </div>
                <div className="space-y-4">
                  {editingBoard.personas.map((persona, index) => (
                    <PersonaEditor
                      key={index}
                      persona={persona}
                      onChange={(updated) => updatePersona(index, updated)}
                      onDelete={() => deletePersona(index)}
                      boardContext={editingBoard.description}
                    />
                  ))}
                </div>
              </div>

              {editingBoard.personas.length > 0 && (
                <BoardQualityScore
                  boardName={editingBoard.name}
                  description={editingBoard.description}
                  personas={editingBoard.personas}
                />
              )}

              <div>
                <Label htmlFor="status">Status</Label>
                <Select
                  value={editingBoard.status}
                  onValueChange={(value: "draft" | "published" | "archived") =>
                    setEditingBoard({ ...editingBoard, status: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsDialogOpen(false);
                    setEditingBoard(null);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit">Save Board</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
      <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Board</AlertDialogTitle>
            <AlertDialogDescription>
              This will archive "{boardToArchive?.name}". You can optionally select a
              replacement board for users currently using this template.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="replacement">Replacement Board (Optional)</Label>
            <Select value={replacementBoardId} onValueChange={setReplacementBoardId}>
              <SelectTrigger>
                <SelectValue placeholder="None - just archive" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">None - just archive</SelectItem>
                {boards
                  .filter(
                    (b) =>
                      b.status === "published" &&
                      b.id !== boardToArchive?.id
                  )
                  .map((board) => (
                    <SelectItem key={board.id} value={board.id}>
                      {board.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setBoardToArchive(null);
              setReplacementBoardId("");
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleArchive}>
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {previewBoard && (
        <BoardPreview
          board={previewBoard}
          open={!!previewBoard}
          onOpenChange={(open) => !open && setPreviewBoard(null)}
        />
      )}
    </>
  );
};
